import { useContext, useState } from 'react';

import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
} from '@repo/videa-components/src/index';
import { Textarea } from '@repo/videa-components/src/ui/textarea';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { TrialStatus } from '../types';
import type { CancelCheckboxReason } from '../types';

import { CancelCheckbox } from './CancelCheckbox';
import {
  ManageSubscriptionButton,
  ManageSubscriptionView,
} from './ManageSubscriptionView';

interface CancelCheckboxOption {
  value: CancelCheckboxReason;
  label: string;
}

interface Props {
  isError: boolean;
  isLoading: boolean;
  onCancel: (reasons: CancelCheckboxReason[], otherReason: string) => void;
  onClose: () => void;
  trialStatus?: TrialStatus;
}

export const CancelConfirmation = ({
  isError,
  isLoading,
  onCancel,
  onClose,
  trialStatus,
}: Props) => {
  const [reasons, setReasons] = useState<Set<CancelCheckboxReason>>(new Set());
  const [otherReason, setOtherReason] = useState('');

  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const externalPatientContext = useContext(ExternalPatientContext);

  const defaultOptions: CancelCheckboxOption[] = [
    {
      value: 'MISSING_FEATURES',
      label: t`Missing features`,
    },
    {
      value: 'NOT_USING_ENOUGH',
      label: t`Not using it enough`,
    },
    {
      value: 'DIFFICULT_TO_USE',
      label: t`Too difficult to use`,
    },
    {
      value: 'COST',
      label: t`Cost (too expensive or budget cuts)`,
    },
    {
      value: 'SWITCHING_TO_OTHER_PRODUCT',
      label: t`Switching to another product`,
    },
    {
      value: 'OTHER',
      label: t`Other`,
    },
  ];

  const isFreeTrial = trialStatus === TrialStatus.TRIAL;

  return (
    <ManageSubscriptionView
      footer={
        <>
          <ManageSubscriptionButton
            onClick={onClose}
            label={t`Stop, Go Back`}
          />
          <Button
            className="vai-w-full"
            disabled={isLoading}
            onClick={() => onCancel(Array.from(reasons), otherReason)}
          >
            {isLoading && (
              <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-2 vai-border-t-transparent" />
            )}
            <p className="vai-font-inter vai-text-sm vai-font-medium">
              <Trans>Continue Cancellation</Trans>
            </p>
          </Button>
          {isError && (
            <Alert variant="destructiveSolid">
              <FontAwesomeIcon icon={faCircleInfo} />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                <Trans>
                  Something went wrong when canceling. Please try again.
                </Trans>
              </AlertDescription>
            </Alert>
          )}
        </>
      }
      onClose={onClose}
      title={isFreeTrial ? t`Cancel Trial` : t`Cancel Subscription`}
    >
      <p className="vai-mt-2 vai-font-inter vai-text-xs vai-font-medium vai-text-red-700">
        <Trans>
          Heads up: Canceling will affect the whole practice, not just this
          machine.
        </Trans>
      </p>

      <p className="vai-font-inter vai-text-zinc-300 vai-text-[12px] vai-uppercase">
        <Trans>Why do you want to cancel?</Trans>
      </p>

      <p className="vai-font-inter vai-text-white vai-text-xs">
        <Trans>Choose one or more</Trans>
      </p>

      {defaultOptions.map((option) => (
        <CancelCheckbox
          checked={reasons.has(option.value)}
          id={option.value}
          key={option.value}
          label={option.label}
          onCheckedChange={(checked) => {
            const newReasons = new Set([...reasons]);
            checked
              ? newReasons.add(option.value)
              : newReasons.delete(option.value);
            setReasons(newReasons);
            trackEvent(mixpanelEvents.externalCancelReasonSelect, {
              ...externalPatientContext,
              reason: option.value,
              status: trialStatus,
              isSelected: checked,
            });
          }}
        />
      ))}

      <p className="vai-mt-4 vai-font-inter vai-text-zinc-50 vai-text-sm vai-font-medium">
        <Trans>Tell us more (optional)</Trans>
      </p>

      <Textarea
        className="vai-bg-transparent vai-border-sidebar-border vai-font-inter vai-text-sm vai-text-white vai-min-h-28"
        placeholder={t`Provide any additional details that may be helpful for us to know...`}
        value={otherReason}
        onChange={(e) => {
          setOtherReason(e.target.value);
        }}
      />
    </ManageSubscriptionView>
  );
};
